// src/common/components/cartModal/components/paymasterPaymentStage/PaymasterPaymentStage.tsx
import { useEffect, useRef } from "react";
import { PaymasterStage } from "../../../../../common/types/enums";
import { useTheme } from "../../../../../common/hooks/useTheme";
import { MdArrowBack, MdCheckCircle, MdError } from "react-icons/md";
import classes from "./PaymasterPaymentStage.module.scss";
import { usePaymasterPayment } from "./usePaymasterPayment";

type Props = {
  paymentUrl: string;
  onSuccess?: () => void;
  onError?: () => void;
  onBack?: () => void;
};

declare global {
  interface Window {
    cpay: {
      PaymentWidget: new () => {
        init: (config: {
          invoice: { url: string };
          onSuccess?: () => void;
          onFail?: () => void;
        }) => void;
      };
    };
  }
}

export const PaymasterPaymentStage = ({
  paymentUrl,
  onSuccess,
  onError,
  onBack,
}: Props) => {
  const { stage } = usePaymasterPayment(paymentUrl, {
    onSuccess,
    onError,
  });
  const { theme } = useTheme();
  const widgetInitialized = useRef(false);

  useEffect(() => {
    if (stage !== PaymasterStage.PAYMENT || widgetInitialized.current) return;

    const script = document.createElement("script");
    script.src = "https://paymaster.ru/cpay/sdk/payment-widget.js";
    script.async = true;

    script.onload = () => {
      if (window.cpay && !widgetInitialized.current) {
        widgetInitialized.current = true;
        const widget = new window.cpay.PaymentWidget();
        widget.init({
          invoice: {
            url: paymentUrl,
          },
          onSuccess: () => {
            onSuccess?.();
          },
          onFail: () => {
            onError?.();
          },
        });
      }
    };

    script.onerror = () => {
      console.error("Failed to load Paymaster widget script");
      onError?.();
    };

    document.head.appendChild(script);

    return () => {
      // Не удаляем скрипт при размонтировании — виджет уже инициализирован
    };
  }, [stage, paymentUrl, onSuccess, onError]);

  if (stage === PaymasterStage.PAYMENT) {
    return (
      <div className={classes.container}>
        <div
          className={classes.header}
          style={{ borderBottomColor: theme.border }}
        >
          <button
            className={classes.backButton}
            onClick={onBack}
            style={{ color: theme.text }}
          >
            <MdArrowBack size={24} />
          </button>
          <div className={classes.titleContainer}>
            <h2 className={classes.title} style={{ color: theme.text }}>
              Оплата заказа
            </h2>
            <p
              className={classes.subtitle}
              style={{ color: theme.textSecondary }}
            >
              Оплата через СБП
            </p>
          </div>
        </div>

        <div className={classes.content}>
          <div
            id="paymaster-widget"
            className={classes.widgetContainer}
            style={{ backgroundColor: theme.card }}
          />
        </div>
      </div>
    );
  }

  if (stage === PaymasterStage.SUCCESS) {
    return (
      <div className={classes.container}>
        <div
          className={classes.header}
          style={{ borderBottomColor: theme.border }}
        >
          <button
            className={classes.backButton}
            onClick={onBack}
            style={{ color: theme.text }}
          >
            <MdArrowBack size={24} />
          </button>
          <div className={classes.titleContainer}>
            <h2 className={classes.title} style={{ color: theme.text }}>
              Оплата заказа
            </h2>
          </div>
        </div>

        <div className={classes.content}>
          <div className={classes.messageContainer}>
            <div
              className={classes.successIcon}
              style={{ backgroundColor: theme.success }}
            >
              <MdCheckCircle size={48} color="#fff" />
            </div>
            <p className={classes.message} style={{ color: theme.text }}>
              Оплата прошла успешно
            </p>
            <p
              className={classes.subMessage}
              style={{ color: theme.textSecondary }}
            >
              Сейчас произойдёт переход на страницу заказа
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === PaymasterStage.ERROR) {
    return (
      <div className={classes.container}>
        <div
          className={classes.header}
          style={{ borderBottomColor: theme.border }}
        >
          <button
            className={classes.backButton}
            onClick={onBack}
            style={{ color: theme.text }}
          >
            <MdArrowBack size={24} />
          </button>
          <div className={classes.titleContainer}>
            <h2 className={classes.title} style={{ color: theme.text }}>
              Оплата заказа
            </h2>
          </div>
        </div>

        <div className={classes.content}>
          <div className={classes.messageContainer}>
            <div
              className={classes.errorIcon}
              style={{ backgroundColor: theme.danger }}
            >
              <MdError size={48} color="#fff" />
            </div>
            <p className={classes.message} style={{ color: theme.text }}>
              Оплата не прошла
            </p>
            <p
              className={classes.subMessage}
              style={{ color: theme.textSecondary }}
            >
              Попробуйте снова или выберите другой способ оплаты
            </p>
            <button
              className={classes.retryButton}
              style={{
                backgroundColor: theme.primary,
                color: "#ffffff",
              }}
              onClick={onBack}
            >
              Вернуться к заказу
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
